import { handleDeerFlowChatWithUI } from './useDeerFlow';
import { handleDeerFlowHistory as apiHandleDeerFlowHistory } from '@/api/grt/ai';
import { clearStoppedState } from './useReportUtils';

/**
 * Thin service wrapper to expose DeerFlow pure logic without React hook dependencies.
 * Both hooks and store can call this function by passing the required callbacks/refs.
 */
export async function createDeerFlowRequest(nextId, msgList, requestChatParams, isNewSession, options = {}) {
    const {
        updateMsgListByCurmsg,
        handleMessages,
        changeSessionMapItem,
        changeOriginMsgMapRef,
        originMsgMapRef,
        isInAnswerRef,
        currentSessionMapIdRef,
        checkIsLogin,
        editPlanMode,
        lastThreadIdRef,
        historyPollingTimers,
        handleDeerFlowChatStream,
        deerFlowApiCall,
        requestSessionId
    } = options || {};

    // Delegate to the existing implementation which already contains the DeerFlow flow logic.
    return handleDeerFlowChatWithUI({
        nextId,
        msgList,
        requestChatParams,
        editPlanMode,
        lastThreadIdRef,
        updateMsgListByCurmsg,
        handleDeerFlowChatStream,
        handleMessages,
        changeSessionMapItem,
        changeOriginMsgMapRef,
        originMsgMapRef,
        isInAnswerRef,
        currentSessionMapIdRef,
        isNewSession,
        deerFlowApiCall,
        requestSessionId,
        historyPollingTimers
    });
}

/**
 * Wrapper for history handling so store can call DeerFlow history logic without hooks.
 */
export async function handleDeerFlowHistoryRequest(session, options = {}) {
    const {
        checkIsLogin,
        formatHistoryRecord,
        changeSessionMapItem,
        changeOriginMsgMapRef,
        originMsgMapRef,
        startHistoryPolling,
        formatSessionList,
        linkToSession,
        currentSessionMapIdRef,
        skillListRef
    } = options || {};

    if (typeof checkIsLogin === 'function') {
        const isLogin = await checkIsLogin();
        if (!isLogin) {
            return false;
        }
    }

    const res = await apiHandleDeerFlowHistory({ thread_id: session.session_id });
    if (res && res.code === 200) {
        const { showMsg, allMsg } = await formatHistoryRecord(res.data.messages, session);
        const taskStatusMap = res.data?.task_status_map || {};
        const currentTaskId = res.data?.current_task_id;
        const hasLoadingMessage = res.data.messages && res.data.messages.some((msg) => msg.status === 'loading');

        const taskIds = Object.keys(taskStatusMap);
        const lastTaskId = taskIds.length > 0 ? taskIds[taskIds.length - 1] : null;
        const currentTaskStatus = currentTaskId ? taskStatusMap[currentTaskId] : null;

        // 默认根据后端的 loading 标记或任务状态判断是否需要轮询
        let needsPolling = hasLoadingMessage || currentTaskStatus === 2;
        // 但如果最新的展示消息中已经有非空的 AI 回复内容，则不应该强制显示 loading（仍可继续后台任务）
        try {
            const lastAiShow = showMsg && Array.isArray(showMsg) ? [...showMsg].reverse().find((m) => m.role === 'ai') : null;
            const lastAiHasContent = lastAiShow && lastAiShow.content && String(lastAiShow.content).trim() !== '';
            if (lastAiHasContent) {
                needsPolling = hasLoadingMessage; // 只有在确实存在 loading 消息时才保留轮询，否则关闭 loading 显示内容
            }
        } catch (e) {
            // ignore
        }

        const sessionState = {
            messages: showMsg,
            loading: needsPolling,
            answering: false
        };

        if (typeof changeSessionMapItem === 'function') {
            changeSessionMapItem(session?.session_id, sessionState, allMsg);
        }

        if (typeof changeOriginMsgMapRef === 'function') {
            const existing = originMsgMapRef && originMsgMapRef.current ? originMsgMapRef.current[session?.session_id] || {} : {};
            changeOriginMsgMapRef({
                [session?.session_id]: {
                    ...existing,
                    messages: allMsg,
                    status: needsPolling ? 0 : null
                }
            });
        }

        if (needsPolling && typeof startHistoryPolling === 'function') {
            startHistoryPolling(session, {
                formatHistoryRecord,
                changeSessionMapItem,
                changeOriginMsgMapRef,
                originMsgMapRef,
                currentSessionMapIdRef,
                skillListRef
            });
        }

        return true;
    }
    return false;
}

/**
 * Handle DeerFlow before-submit logic in non-hook contexts (e.g., store).
 * Mirrors useDeerFlow.handleDeerFlowBeforeSubmit behavior.
 */
export function handleDeerFlowBeforeSubmit(requestChatParams, isNewSession, options = {}) {
    const { currentSessionMapIdRef, sessionListRef, formatSessionList, linkToSession, changeOriginMsgMapRef, originMsgMapRef, editPlanMode = false } = options;

    let session_id = requestChatParams?.session_id;

    if (isNewSession) {
        // DeerFlow 新会话：session_id 设为空字符串，等待后端返回 thread_id
        session_id = '';
        if (currentSessionMapIdRef && typeof currentSessionMapIdRef === 'object') {
            currentSessionMapIdRef.current = '';
        }

        requestChatParams.session_key = session_id;
        let curList = sessionListRef && sessionListRef.current ? sessionListRef.current : [];
        // 仅在当前会话列表为空或首项为占位（'新会话'）时插入占位项，避免覆盖已有有意义的会话名称
        const needInsertPlaceholder = !Array.isArray(curList) || curList.length === 0 || !curList[0] || !curList[0].session_name || curList[0].session_name === '新会话';
        if (needInsertPlaceholder) {
            // 添加 handler_json 保证会话在刷新后仍能识别为 deerflow
            let data = [
                {
                    session_name: '新会话',
                    time_range: '1',
                    session_id: session_id,
                    skill_id: requestChatParams?.skill_id,
                    handler_json: ['deerflow']
                },
                ...curList
            ];
            if (typeof formatSessionList === 'function') {
                formatSessionList(data);
            }
        }
    }

    if (!requestChatParams.session_id || requestChatParams.session_id === 0) {
        requestChatParams.session_id = '';
    }

    // 如果不是编辑计划模式，且不是 start_research，则删除 threadId
    if (!editPlanMode && requestChatParams.value !== 'start_research') {
        delete requestChatParams.threadId;
    }

    const currentSessionId = (currentSessionMapIdRef && currentSessionMapIdRef.current) || session_id;

    // 在用户发送新消息前，尝试清除可能的用户停止标记（避免用户此前 stop 导致后续发送被挂起）
    try {
        const possibleIds = [];
        if (requestChatParams && requestChatParams.threadId) {
            possibleIds.push(requestChatParams.threadId);
        }
        // 从 originMsgMapRef 中查找最近的 task_id
        const existing = originMsgMapRef && originMsgMapRef.current ? originMsgMapRef.current[currentSessionId] || {} : {};
        const msgs = existing.messages || [];
        const lastTaskMsg = Array.isArray(msgs) && msgs.length ? [...msgs].reverse().find((m) => m && m.task_id) : null;
        if (lastTaskMsg && lastTaskMsg.task_id) {
            possibleIds.push(lastTaskMsg.task_id);
        }
        // 尝试也清理 sessionId 自身（有些实现把 sessionId 作为 reportId）
        if (currentSessionId) possibleIds.push(currentSessionId);

        possibleIds.forEach((id) => {
            try {
                if (id) clearStoppedState(id);
            } catch (e) {
                // 忽略单个清理失败
            }
        });
    } catch (e) {
        // 忽略清理步骤的任何意外错误
    }

    // 安全地更新 origin map，设置为 loading 状态
    if (typeof changeOriginMsgMapRef === 'function') {
        const existing = originMsgMapRef && originMsgMapRef.current ? originMsgMapRef.current[currentSessionId] || {} : {};
        changeOriginMsgMapRef({
            [currentSessionId]: {
                ...existing,
                status: 0
            }
        });
    }

    return { session_id, currentSessionId };
}


